"use client";

import { motion } from "framer-motion";
import React from "react";

/**
 * @typedef {Object} MarqueeImage
 * @property {string} src
 * @property {string} alt
 * @property {string} [href]
 * @property {"_blank" | "_self" | "_parent" | "_top"} [target]
 */

/**
 * @typedef {Object} ThreeDMarqueeProps
 * @property {MarqueeImage[]} images
 * @property {string} [className]
 * @property {number} [cols]
 * @property {(image: MarqueeImage, index: number) => void} [onImageClick]
 */

export const ThreeDMarquee = ({
  images,
  className = "",
  cols = 4,
  onImageClick,
}) => {
  const duplicatedImages = [...images, ...images];

  const groupSize = Math.ceil(duplicatedImages.length / cols);
  const imageGroups = Array.from({ length: cols }, (_, index) =>
    duplicatedImages.slice(index * groupSize, (index + 1) * groupSize)
  );

  const handleImageClick = (image, globalIndex) => {
    if (onImageClick) {
      onImageClick(image, globalIndex);
    } else if (image.href) {
      window.open(image.href, image.target || "_self");
    }
  };

  return (
    <section
      className={`mx-auto block h-[500px] sm:h-[600px] overflow-hidden 
    rounded-2xl bg-transparent ${className}`}
    >
      <div
        className="flex w-full h-full items-center justify-center"
        style={{
          transform: "rotateX(55deg) rotateZ(45deg)",
        }}
      >
        <div className="w-full overflow-hidden scale-90 sm:scale-100">
          <div
            className={`relative grid h-full w-full origin-center 
              grid-cols-2 sm:grid-cols-${cols} gap-4`}
          >
            {imageGroups.map((imagesInGroup, idx) => (
              <motion.div
                key={`column-${idx}`}
                animate={{ y: idx % 2 === 0 ? 100 : -100 }}
                transition={{
                  duration: idx % 2 === 0 ? 12 : 15,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="flex flex-col items-center gap-6 relative"
              >
                {imagesInGroup.map((image, imgIdx) => {
                  const globalIndex = idx * groupSize + imgIdx;
                  const isClickable = image.href || onImageClick;

                  return (
                    <motion.img
                      key={`img-${globalIndex}`}
                      whileHover={{ y: -10, scale: 1.05 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      src={image.src}
                      alt={image.alt}
                      width={300}
                      height={200}
                      className={`aspect-[4/3] w-full max-w-[220px] rounded-lg object-cover 
                        ring ring-gray-300/30 dark:ring-gray-800/50 shadow-xl 
                        hover:shadow-2xl transition-shadow duration-300 
                        ${isClickable ? "cursor-pointer" : ""}`}
                      onClick={() => handleImageClick(image, globalIndex)}
                    />
                  );
                })}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
